import {
  CLIENT_BUILD_ID,
  FEEDBACK_CLOSED_STATUSES,
  FEEDBACK_GROUP_NONE,
  FEEDBACK_STATUSES,
  FEEDBACK_TYPES,
  type FeedbackType,
  type OperatorFeedbackQuery,
  type FeedbackStatus as Status,
  uuidSchema,
} from "@esi/shared";
import type { FeedbackReportOrigin } from "../router.tsx";

/**
 * The Feedback inbox's filters, kept in its query string (openspec change `feedback-triage-ux`
 * D1): `?status=&type=&tenant=&build=&group=`. Read leniently (an unknown value reads as "any"), written
 * canonically, so the same filters always give the same URL and nothing else passes through.
 */
export type FeedbackFilters = {
  status: Status | "all";
  type: FeedbackType | "all";
  tenantId: string;
  build: string;
  /** Any group, Unsorted (`none`, in no group) or one group's ID (feedback-inbox-followups D2). */
  group: "all" | typeof FEEDBACK_GROUP_NONE | (string & {});
};
export const NO_FILTERS: FeedbackFilters = {
  status: "all",
  type: "all",
  tenantId: "",
  build: "",
  group: "all",
};

/** A group ID as the API's schema takes it (an RFC UUID): anything else reads as any group. */
const isGroupId = (value: string) => uuidSchema.safeParse(value.toLowerCase()).success;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** What the Build field keeps while typed: a build ID's characters, at most 64 of them. */
const draftBuild = (value: string) => value.replace(/[^0-9A-Za-z.-]/g, "").slice(0, 64);
const oneOf = <T extends string>(values: readonly T[], value: string | null): T | null =>
  value !== null && (values as readonly string[]).includes(value) ? (value as T) : null;

export function filtersOf(search: URLSearchParams): FeedbackFilters {
  const tenant = search.get("tenant") ?? "";
  const group = search.get("group") ?? "";
  return {
    status: oneOf(FEEDBACK_STATUSES, search.get("status")) ?? "all",
    type: oneOf(FEEDBACK_TYPES, search.get("type")) ?? "all",
    tenantId: UUID.test(tenant) ? tenant.toLowerCase() : "",
    build: draftBuild(search.get("build") ?? ""),
    group: group === FEEDBACK_GROUP_NONE ? group : isGroupId(group) ? group.toLowerCase() : "all",
  };
}

/** The canonical query string of `filters`, without `?`; empty when nothing is filtered. */
export function searchOf(filters: FeedbackFilters): string {
  const query = new URLSearchParams();
  if (filters.status !== "all") {
    query.set("status", filters.status);
  }
  if (filters.type !== "all") {
    query.set("type", filters.type);
  }
  if (filters.tenantId) {
    query.set("tenant", filters.tenantId);
  }
  if (draftBuild(filters.build)) {
    query.set("build", draftBuild(filters.build));
  }
  if (filters.group === FEEDBACK_GROUP_NONE || isGroupId(filters.group)) {
    query.set("group", filters.group);
  }
  return query.toString();
}

/** The API query of `filters`: a partial build ID is not sent (the API takes whole ones). */
export function queryOf(filters: FeedbackFilters): OperatorFeedbackQuery {
  return {
    ...(filters.status !== "all" && { status: filters.status }),
    ...(filters.type !== "all" && { type: filters.type }),
    ...(filters.tenantId && { tenantId: filters.tenantId }),
    ...(CLIENT_BUILD_ID.test(filters.build) && { build: filters.build }),
    ...((filters.group === FEEDBACK_GROUP_NONE || isGroupId(filters.group)) && {
      group: filters.group,
    }),
  };
}

/**
 * A report's way back (D2): its `?group=` (a group ID) or its `?inbox=` (re-read filters). Null
 * for a pasted link that carries neither, which steps nowhere (feedback-inbox-followups D5) and
 * leads back to the unfiltered inbox.
 */
export function stepOriginOf(search: URLSearchParams): FeedbackReportOrigin | null {
  return search.has("group") || search.has("inbox") ? originOf(search) : null;
}

export function originOf(search: URLSearchParams): FeedbackReportOrigin {
  const group = search.get("group") ?? "";
  if (UUID.test(group)) {
    return { group: group.toLowerCase() };
  }
  return { inbox: searchOf(filtersOf(new URLSearchParams(search.get("inbox") ?? ""))) };
}

/** A group the operator can still file reports into (its status is not a closing one). */
export const isOpenStatus = (status: Status) =>
  !(FEEDBACK_CLOSED_STATUSES as readonly string[]).includes(status);
